import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Button } from '@/shared/ui/Button';
import { Select } from '@/shared/ui/Select';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { ReviewReplyFormality, ReviewReplyPatientAddressing } from '../api/reviewsApi';
import { useLocation } from '@/shared/hooks/useLocation';
import { useReviewSettings } from '../hooks/useReviewSettings';
import styles from './ReviewSettingsPanel.module.css';

const ADDRESSING_OPTIONS = [
  { value: 'no_name', label: 'No name (never address the patient by name)' },
  { value: 'first_name', label: 'Use first name (e.g. "Thank you, Sarah!")' },
  { value: 'title_last_name', label: 'Title + last name only (e.g. "Dr. Smith")' },
];

const FORMALITY_OPTIONS = [
  { value: 'formal', label: 'Formal — professional, no contractions' },
  { value: 'professional', label: 'Professional — standard clinical tone' },
  { value: 'warm_casual', label: 'Warm & casual — conversational' },
];

const THRESHOLD_OPTIONS = [
  { value: '5', label: '5★ only — draft replies for 5-star reviews' },
  { value: '4', label: '4★ and 5★ — draft replies for 4- and 5-star reviews' },
];

/**
 * Review settings for the current location. `part` shows one half on its own, each saving only its own fields:
 * "responder" (how Google review replies are drafted) and "capture" (the Review Accelerator's patient flow).
 */
export function ReviewSettingsPanel({
  onClose,
  part = 'all',
}: {
  onClose?: () => void;
  part?: 'all' | 'responder' | 'capture';
}) {
  const showResponder = part !== 'capture';
  const showCapture = part !== 'responder';
  const { currentLocation } = useLocation();
  const { data, update } = useReviewSettings();
  const [signature, setSignature] = useState('');
  const [patientAddressing, setPatientAddressing] =
    useState<ReviewReplyPatientAddressing>('no_name');
  const [formality, setFormality] = useState<ReviewReplyFormality>('professional');
  const [voiceNotes, setVoiceNotes] = useState('');
  const [autoDraftThreshold, setAutoDraftThreshold] = useState(5);
  const [aeoEnabled, setAeoEnabled] = useState(true);
  const [captureReasonStepEnabled, setCaptureReasonStepEnabled] = useState(true);

  useEffect(() => {
    if (data) {
      setSignature(data.signature ?? data.suggestedSignature ?? '');
      setPatientAddressing(data.patientAddressing ?? 'no_name');
      setFormality(data.formality ?? 'professional');
      setVoiceNotes(data.voiceNotes ?? '');
      setAutoDraftThreshold(data.autoDraftThreshold ?? 5);
      setAeoEnabled(data.aeoEnabled ?? true);
      setCaptureReasonStepEnabled(data.captureReasonStepEnabled ?? true);
    }
  }, [data]);

  const responderDirty =
    !!data &&
    (signature.trim() !== (data.signature ?? '').trim() ||
      patientAddressing !== data.patientAddressing ||
      formality !== data.formality ||
      voiceNotes.trim() !== (data.voiceNotes ?? '').trim() ||
      autoDraftThreshold !== (data.autoDraftThreshold ?? 5) ||
      aeoEnabled !== (data.aeoEnabled ?? true));
  const captureDirty =
    !!data && captureReasonStepEnabled !== (data.captureReasonStepEnabled ?? true);
  const dirty = (showResponder && responderDirty) || (showCapture && captureDirty);

  const handleSave = () => {
    update.mutate({
      ...(showResponder
        ? {
            signature: signature.trim(),
            patientAddressing,
            formality,
            voiceNotes: voiceNotes.trim() || null,
            autoDraftThreshold,
            aeoEnabled,
          }
        : {}),
      ...(showCapture ? { captureReasonStepEnabled } : {}),
    });
  };

  return (
    <div className={clsx(patterns.panel, styles.panel)}>
      <div className={styles.formRows}>
        {showResponder && (
          <>
            <Select
              label="Patient addressing"
              options={ADDRESSING_OPTIONS}
              value={patientAddressing}
              onChange={(e) => setPatientAddressing(e.target.value as ReviewReplyPatientAddressing)}
              fullWidth
            />
            {patientAddressing === 'first_name' && (
              <span className={styles.hipaaWarning}>
                HIPAA risk — using a reviewer's name alongside any clinical context is a privacy
                violation. The AI will flag replies where this occurs.
              </span>
            )}

            <Select
              label="Tone"
              options={FORMALITY_OPTIONS}
              value={formality}
              onChange={(e) => setFormality(e.target.value as ReviewReplyFormality)}
              fullWidth
            />

            <Select
              label="Auto-draft threshold"
              options={THRESHOLD_OPTIONS}
              value={String(autoDraftThreshold)}
              onChange={(e) => setAutoDraftThreshold(Number(e.target.value))}
              hint="Lower-rated reviews are flagged for manual attention. You can always write a reply manually for any review."
              fullWidth
            />

            <div className={styles.section}>
              <label className={styles.fieldLabel} htmlFor="review-signature">
                Reply sign-off
              </label>
              <textarea
                id="review-signature"
                className={patterns.textarea}
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder={`e.g.\nRegards,\n${currentLocation?.name ?? 'Your Practice'}`}
                rows={3}
                maxLength={200}
              />
              <span className={styles.hint}>
                {data?.suggestedSignature && !data.signature
                  ? 'Suggested from your past replies — edit if needed. Use line breaks for multi-line sign-offs.'
                  : 'Use line breaks for a multi-line sign-off (e.g. "Regards," then your name).'}
              </span>
            </div>

            <div className={styles.section}>
              <label className={styles.fieldLabel} htmlFor="review-voice-notes">
                Additional voice guidelines
              </label>
              <textarea
                id="review-voice-notes"
                className={patterns.textarea}
                value={voiceNotes}
                onChange={(e) => setVoiceNotes(e.target.value)}
                placeholder="e.g. We never mention specific treatments in replies. We prefer short, direct sentences."
                rows={3}
                maxLength={500}
              />
              <span className={styles.hint}>
                Any specific instructions for the AI — the model will follow these for every reply.
              </span>
            </div>

            <div className={styles.section}>
              <label className={styles.fieldLabel}>Local SEO (AEO)</label>
              <div className={styles.checkboxRow}>
                <input
                  id="review-aeo"
                  type="checkbox"
                  className={styles.checkbox}
                  checked={aeoEnabled}
                  onChange={(e) => setAeoEnabled(e.target.checked)}
                />
                <label htmlFor="review-aeo" className={styles.checkboxLabel}>
                  Include location & service category in replies
                </label>
              </div>
              <span className={styles.hint}>
                Naturally weaves your city and specialty into replies to support local search
                visibility. The AI skips this when it would feel forced.
              </span>
            </div>
          </>
        )}

        {showCapture && (
          <div className={styles.section}>
            <label className={styles.fieldLabel}>Review capture privacy</label>
            <div className={styles.checkboxRow}>
              <input
                id="review-capture-reason"
                type="checkbox"
                className={styles.checkbox}
                checked={captureReasonStepEnabled}
                onChange={(e) => setCaptureReasonStepEnabled(e.target.checked)}
              />
              <label htmlFor="review-capture-reason" className={styles.checkboxLabel}>
                Ask patients “What brought you in?” in the review flow
              </label>
            </div>
            <span className={styles.hint}>
              Optional, category-level chips only (never a specific procedure or diagnosis), and the
              patient is told the answer may appear in their public review. Turn off if your
              practice prefers not to ask at all.
            </span>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button size="sm" onClick={handleSave} isLoading={update.isPending} disabled={!dirty}>
          Save settings
        </Button>
        {onClose && (
          <Button size="sm" variant="secondary" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
    </div>
  );
}
